import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://kvinternational.be";
const OG_IMAGE = "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/products/783935758/5194593295.jpg";

const DEFAULT_TITLE = "KV International — Achat et Vente de Porsche de prestige en Belgique · Dépôt-vente d'exception · Rhode-Saint-Genèse";
const DEFAULT_DESCRIPTION =
  "Achat et vente de Porsche de prestige en Belgique. Dépôt-vente de véhicules d'exception, achat cash 24-48 h, recherche personnalisée et coaching pilotage sur simulateur pro. Rhode-Saint-Genèse, sur rendez-vous.";

/**
 * SEO — sets title / description / OpenGraph / Twitter meta on every page.
 * schema : "AutoDealer" adapté (schema.org officiel pour concessionnaires) ; sur les fiches véhicule, le composant passe schemaType="Vehicle" et vehicle={obj}.
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = OG_IMAGE,
  fullTitle = false,
  type = "website",
  schemaType = "AutoDealer", // AutoDealer | Vehicle
  vehicle = null, // objet Vehicle utilisé pour le schema si schemaType==="Vehicle"
}) {
  const { pathname } = useLocation();
  const url = `${SITE_URL}${pathname}`;
  const finalTitle = fullTitle || !title ? (title || DEFAULT_TITLE) : `${title} · KV International — Rhode-Saint-Genèse`;

  const autoDealer = {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    name: "KV International",
    image,
    url: SITE_URL,
    telephone: "+32 486 52 19 06",
    email: "contact@kvinternational.be",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Chaussée de Waterloo 242",
      postalCode: "1640",
      addressLocality: "Rhode-Saint-Genèse",
      addressRegion: "Brabant flamand",
      addressCountry: "BE",
    },
    areaServed: { "@type": "Country", name: "Belgium" },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      description: "By appointment only",
    },
    priceRange: "€€€",
    sameAs: ["https://kvinternational.be"],
  };

  const vehicleSchema = vehicle
    ? {
        "@context": "https://schema.org",
        "@type": "Vehicle",
        name: `${vehicle.brand} ${vehicle.model} ${vehicle.year || ""}`.trim(),
        description: `${vehicle.brand} ${vehicle.model} ${vehicle.year || ""} d'occasion prestige — ${vehicle.mileage ? vehicle.mileage + " km · " : ""}${vehicle.power_hp || ""} ch · ${vehicle.fuel_type || ""} · ${vehicle.transmission || ""}. Sélectionné et garanti par KV International, spécialiste de l'achat et vente de Porsche de prestige en Belgique.`,
        sku: vehicle.id,
        brand: { "@type": "Brand", name: vehicle.brand },
        manufacturer: { "@type": "Organization", name: vehicle.brand },
        model: vehicle.model,
        vehicleModelDate: vehicle.year,
        productionDate: vehicle.year ? String(vehicle.year) : undefined,
        modelDate: vehicle.year ? String(vehicle.year) : undefined,
        itemCondition: "https://schema.org/UsedCondition",
        mileageFromOdometer: vehicle.mileage
          ? { "@type": "QuantitativeValue", value: vehicle.mileage, unitCode: "KMT" }
          : undefined,
        fuelType: vehicle.fuel_type,
        vehicleTransmission: vehicle.transmission,
        color: vehicle.color || undefined,
        vehicleInteriorColor: vehicle.interior_color || undefined,
        bodyType: vehicle.body_type || undefined,
        driveWheelConfiguration: vehicle.drive || undefined,
        numberOfDoors: vehicle.doors || undefined,
        vehicleEngine: vehicle.power_hp
          ? {
              "@type": "EngineSpecification",
              enginePower: { "@type": "QuantitativeValue", value: vehicle.power_hp, unitCode: "BHP" },
              fuelType: vehicle.fuel_type,
            }
          : undefined,
        image: vehicle.images && vehicle.images.length > 0 ? vehicle.images.slice(0, 5) : [image],
        url,
        offers:
          vehicle.price && !vehicle.price_confidential
            ? {
                "@type": "Offer",
                price: vehicle.price,
                priceCurrency: "EUR",
                itemCondition: "https://schema.org/UsedCondition",
                availability:
                  vehicle.status === "sold"
                    ? "https://schema.org/SoldOut"
                    : vehicle.status === "reserved"
                    ? "https://schema.org/LimitedAvailability"
                    : "https://schema.org/InStock",
                priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
                url,
                seller: {
                  "@type": "AutoDealer",
                  name: "KV International",
                  url: SITE_URL,
                  telephone: "+32 486 52 19 06",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "Chaussée de Waterloo 242",
                    postalCode: "1640",
                    addressLocality: "Rhode-Saint-Genèse",
                    addressCountry: "BE",
                  },
                },
              }
            : undefined,
      }
    : null;

  const schema = schemaType === "Vehicle" && vehicleSchema ? vehicleSchema : autoDealer;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content="achat Porsche Belgique, vente Porsche Belgique, Porsche prestige Belgique, dépôt-vente Porsche, dépôt-vente voiture d'exception, vendre sa Porsche, acheter une Porsche occasion, Porsche 911 occasion Belgique, Porsche 992, Porsche 991, Porsche GT3, Porsche Turbo, Rhode-Saint-Genèse, spécialiste Porsche Belgique, coaching pilotage simulateur, simulateur de conduite pro" />
      <meta name="geo.region" content="BE-VBR" />
      <meta name="geo.placename" content="Rhode-Saint-Genèse" />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="fr-BE" href={url} />
      <link rel="alternate" hrefLang="nl-BE" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="KV International" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="fr_BE" />
      <meta property="og:locale:alternate" content="nl_BE" />
      <meta property="og:locale:alternate" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD schema.org */}
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
